import { useState, useCallback, useRef, useEffect } from 'react';
import { getDataWithQueryString } from '@componentAPIs';
import { RMAXLogger } from '@RMAXLogger';
import { ImageGroup, ApiConfig } from '../types';
import { transformResponse } from '../utils/transformResponse';
import { MESSAGES } from '../Popup/constants';

interface UseProImagesResult {
  imageGroups: ImageGroup[];
  loading: boolean;
  error: string | null;
  fetchImages: () => Promise<void>;
}

export const useProImages = (
  apiConfig: ApiConfig | undefined,
  proNumber?: string
): UseProImagesResult => {
  const [imageGroups, setImageGroups] = useState<ImageGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const configRef = useRef(apiConfig);
  useEffect(() => {
    configRef.current = apiConfig;
  }, [apiConfig]);

  const fetchImages = useCallback(async () => {
    if (!proNumber) {
      setError(MESSAGES.PRO_NUMBER_NOT_AVAILABLE);
      setLoading(false);
      return;
    }

    // If no apiConfig provided, skip fetching (for iframe-only flows)
    if (!configRef.current) {
      setImageGroups([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let responseData: any;

      if (configRef.current.fetchCallback) {
        responseData = await configRef.current.fetchCallback(proNumber);
      } else {
        const queryParams = configRef.current.buildQueryParams?.(proNumber) || `proId=${proNumber}`;
        const endpoint = configRef.current.endpoint || 'GetProImages';

        const response = await getDataWithQueryString(endpoint, queryParams);

        if (response && Math.trunc(response.status / 100) === 2 && response.data) {
          responseData = response.data;
        } else {
          setError(MESSAGES.FAILED_TO_FETCH_IMAGES);
          setLoading(false);
          setImageGroups([]);

          RMAXLogger.error('ProImagesRouteMax: API returned error', {
            proNumber,
            status: response.status
          });
          return;
        }
      }

      let groups: ImageGroup[];
      
      if (Array.isArray(responseData)) {
        groups = responseData;
      } else if (configRef.current.transformConfig) {
        // Use generic transformation utility
        groups = transformResponse(responseData, configRef.current.transformConfig);
      } else if (configRef.current.transformResponse) {
        // Use custom transformation function
        groups = configRef.current.transformResponse(responseData);
      } else {
        groups = [];
      }
      
      setImageGroups(groups);
      setLoading(false);
      setError(null);

      const totalImages = groups.reduce((sum, group) => sum + group.images.length, 0);
      RMAXLogger.info('ProImagesRouteMax: Images loaded successfully', {
        proNumber,
        groups: groups.length,
        totalImages
      });
    } catch (err: any) {
      RMAXLogger.error('ProImagesRouteMax: Failed to fetch images', {
        error: err.message,
        proNumber,
        status: err.response?.status
      });

      setError(err.response?.data?.message || err.message || MESSAGES.FAILED_TO_LOAD_IMAGES);
      setLoading(false);
      setImageGroups([]);
    }
  }, [proNumber]);

  return {
    imageGroups,
    loading,
    error,
    fetchImages
  };
};
